<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import FollowUpMessagesAPI from 'dashboard/api/followUpMessages';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  funnelId: {
    type: [Number, String],
    required: true,
  },
  stageName: {
    type: String,
    required: true,
  },
  stageLabel: {
    type: String,
    default: '',
  },
  stageColor: {
    type: String,
    default: '#6366f1',
  },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();

// State
const messages = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const editingMessage = ref(null);
const showAddForm = ref(false);

// New message form
const newMessage = ref({
  name: '',
  enabled: true,
  trigger: 'on_entry',
  delay_minutes: 0,
  message_type: 'text',
  content: '',
  template_id: null,
  template_params: {},
  channels: ['whatsapp'],
});

// WhatsApp templates from inboxes
const whatsappTemplates = ref([]);

// Delay options
const delayOptions = [
  { value: 0, label: 'Imediato' },
  { value: 1, label: '1 minuto' },
  { value: 5, label: '5 minutos' },
  { value: 15, label: '15 minutos' },
  { value: 30, label: '30 minutos' },
  { value: 60, label: '1 hora' },
  { value: 120, label: '2 horas' },
  { value: 360, label: '6 horas' },
  { value: 720, label: '12 horas' },
  { value: 1440, label: '24 horas' },
];

// Available variables
const variables = [
  { key: '{contact_name}', label: 'Nome do contato' },
  { key: '{contact_email}', label: 'Email do contato' },
  { key: '{contact_phone}', label: 'Telefone do contato' },
  { key: '{stage_label}', label: 'Nome da etapa atual' },
  { key: '{stage_previous}', label: 'Nome da etapa anterior' },
  { key: '{funnel_name}', label: 'Nome do funil' },
  { key: '{agent_name}', label: 'Nome do agente' },
  { key: '{deal_value}', label: 'Valor do negócio' },
];

// Computed
const hasChanges = computed(() => {
  return messages.value.some(m => m._modified);
});

const messageCount = computed(() => messages.value.length);

const enabledCount = computed(() => messages.value.filter(m => m.enabled).length);

// Methods
const loadMessages = async () => {
  if (!props.funnelId || !props.stageName) return;

  isLoading.value = true;
  try {
    const response = await FollowUpMessagesAPI.getMessages(
      props.funnelId,
      props.stageName
    );
    messages.value = (response.data.follow_up_messages || []).map(m => ({
      ...m,
      _modified: false,
    }));
  } catch (error) {
    useAlert('Erro ao carregar mensagens de follow-up');
  } finally {
    isLoading.value = false;
  }
};

const loadWhatsappTemplates = async () => {
  try {
    const inboxes = store.getters['inboxes/getInboxes'] || [];
    const whatsappInboxes = inboxes.filter(
      i => i.channel_type === 'Channel::Whatsapp'
    );

    const templates = [];
    for (const inbox of whatsappInboxes) {
      try {
        const response = await store.dispatch('getWhatsappTemplates', {
          inboxId: inbox.id,
        });
        if (response?.data) {
          const approvedTemplates = response.data.filter(
            t => t.status === 'approved'
          );
          templates.push(
            ...approvedTemplates.map(t => ({
              ...t,
              inbox_name: inbox.name,
              inbox_id: inbox.id,
            }))
          );
        }
      } catch {
        // Skip inbox if templates fail to load
      }
    }
    whatsappTemplates.value = templates;
  } catch {
    // Silently fail
  }
};

const resetNewMessage = () => {
  newMessage.value = {
    name: '',
    enabled: true,
    trigger: 'on_entry',
    delay_minutes: 0,
    message_type: 'text',
    content: '',
    template_id: null,
    template_params: {},
    channels: ['whatsapp'],
  };
  showAddForm.value = false;
};

const addMessage = async () => {
  if (!newMessage.value.name) {
    useAlert('Digite um nome para a mensagem');
    return;
  }

  if (
    newMessage.value.message_type === 'text' &&
    !newMessage.value.content
  ) {
    useAlert('Digite o conteúdo da mensagem');
    return;
  }

  isSaving.value = true;
  try {
    const response = await FollowUpMessagesAPI.createMessage(
      props.funnelId,
      props.stageName,
      newMessage.value
    );
    messages.value.push({ ...response.data, _modified: false });
    resetNewMessage();
    useAlert('Mensagem adicionada com sucesso!');
    emit('saved');
  } catch (error) {
    useAlert('Erro ao adicionar mensagem');
  } finally {
    isSaving.value = false;
  }
};

const updateMessage = async message => {
  isSaving.value = true;
  try {
    const { _modified, ...messageData } = message;
    await FollowUpMessagesAPI.updateMessage(
      props.funnelId,
      props.stageName,
      message.id,
      messageData
    );
    message._modified = false;
    useAlert('Mensagem atualizada!');
    emit('saved');
  } catch (error) {
    useAlert('Erro ao atualizar mensagem');
  } finally {
    isSaving.value = false;
  }
};

const deleteMessage = async message => {
  if (!confirm('Tem certeza que deseja excluir esta mensagem?')) return;

  isSaving.value = true;
  try {
    await FollowUpMessagesAPI.deleteMessage(
      props.funnelId,
      props.stageName,
      message.id
    );
    messages.value = messages.value.filter(m => m.id !== message.id);
    useAlert('Mensagem excluída!');
    emit('saved');
  } catch (error) {
    useAlert('Erro ao excluir mensagem');
  } finally {
    isSaving.value = false;
  }
};

const toggleEnabled = message => {
  message.enabled = !message.enabled;
  message._modified = true;
  updateMessage(message);
};

const markModified = message => {
  message._modified = true;
};

const insertVariable = (message, variable) => {
  if (message) {
    message.content = (message.content || '') + variable;
    message._modified = true;
  } else {
    newMessage.value.content = (newMessage.value.content || '') + variable;
  }
};

const getDelayLabel = minutes => {
  const option = delayOptions.find(o => o.value === minutes);
  return option ? option.label : `${minutes} min`;
};

const closeModal = () => {
  if (hasChanges.value) {
    if (!confirm('Existem alterações não salvas. Deseja sair mesmo assim?')) {
      return;
    }
  }
  emit('close');
};

// Watchers
watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      loadMessages();
      loadWhatsappTemplates();
    }
  }
);

// Lifecycle
onMounted(() => {
  if (props.isOpen) {
    loadMessages();
    loadWhatsappTemplates();
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-n-black/80 backdrop-blur-sm"
        @click="closeModal"
      />

      <!-- Modal -->
      <div
        class="relative z-10 w-full max-w-2xl max-h-[90vh] mx-4 bg-n-solid-2 rounded-xl shadow-2xl flex flex-col overflow-hidden"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          :style="{ borderTopColor: stageColor, borderTopWidth: '3px' }"
        >
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Mensagens de Follow-up
            </h2>
            <p class="text-sm text-n-slate-11 mt-0.5">
              Etapa: <span class="font-medium">{{ stageLabel || stageName }}</span>
              <span v-if="messageCount > 0" class="ml-2 text-n-slate-10">
                ({{ enabledCount }}/{{ messageCount }} ativas)
              </span>
            </p>
          </div>
          <button
            class="p-2 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors"
            @click="closeModal"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- Loading -->
          <div v-if="isLoading" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-woot-500 border-t-transparent" />
          </div>

          <!-- Messages List -->
          <div v-else class="space-y-4">
            <!-- Existing Messages -->
            <div
              v-for="message in messages"
              :key="message.id"
              class="rounded-lg border border-n-weak bg-n-solid-3 overflow-hidden"
            >
              <!-- Message Header -->
              <div class="flex items-center justify-between px-4 py-3 bg-n-alpha-1">
                <div class="flex items-center gap-3">
                  <button
                    class="p-1 rounded transition-colors"
                    :class="message.enabled ? 'text-n-teal-11 hover:bg-n-teal-3' : 'text-n-slate-10 hover:bg-n-alpha-2'"
                    :title="message.enabled ? 'Desativar' : 'Ativar'"
                    @click="toggleEnabled(message)"
                  >
                    <span
                      :class="message.enabled ? 'i-lucide-toggle-right' : 'i-lucide-toggle-left'"
                      class="size-5"
                    />
                  </button>
                  <div>
                    <input
                      v-model="message.name"
                      class="text-sm font-medium text-n-slate-12 bg-transparent border-none p-0 focus:outline-none focus:ring-0"
                      placeholder="Nome da mensagem"
                      @input="markModified(message)"
                    />
                    <div class="flex items-center gap-2 mt-0.5 text-xs text-n-slate-10">
                      <span>{{ message.message_type === 'template' ? 'Template' : 'Texto' }}</span>
                      <span class="text-n-slate-8">|</span>
                      <span>{{ getDelayLabel(message.delay_minutes) }}</span>
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="message._modified"
                    class="px-3 py-1.5 text-xs font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors"
                    :disabled="isSaving"
                    @click="updateMessage(message)"
                  >
                    Salvar
                  </button>
                  <button
                    class="p-1.5 text-n-ruby-11 hover:bg-n-ruby-3 rounded transition-colors"
                    title="Excluir"
                    @click="deleteMessage(message)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </div>

              <!-- Message Content -->
              <div class="px-4 py-3 space-y-3">
                <!-- Delay -->
                <div class="flex items-center gap-3">
                  <label class="text-xs font-medium text-n-slate-11 w-20">Delay:</label>
                  <select
                    v-model="message.delay_minutes"
                    class="flex-1 h-8 px-3 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 focus:border-woot-500 focus:outline-none"
                    @change="markModified(message)"
                  >
                    <option
                      v-for="option in delayOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <!-- Message Type -->
                <div class="flex items-center gap-3">
                  <label class="text-xs font-medium text-n-slate-11 w-20">Tipo:</label>
                  <div class="flex gap-2">
                    <button
                      class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                      :class="message.message_type === 'text' ? 'bg-woot-500 text-white' : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
                      @click="message.message_type = 'text'; markModified(message)"
                    >
                      Texto
                    </button>
                    <button
                      class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                      :class="message.message_type === 'template' ? 'bg-woot-500 text-white' : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
                      @click="message.message_type = 'template'; markModified(message)"
                    >
                      Template WhatsApp
                    </button>
                  </div>
                </div>

                <!-- Text Content -->
                <div v-if="message.message_type === 'text'">
                  <div class="flex items-center justify-between mb-1">
                    <label class="text-xs font-medium text-n-slate-11">Conteúdo:</label>
                    <div class="flex gap-1">
                      <button
                        v-for="variable in variables"
                        :key="variable.key"
                        class="px-1.5 py-0.5 text-[10px] bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 rounded transition-colors"
                        :title="variable.label"
                        @click="insertVariable(message, variable.key)"
                      >
                        {{ variable.key }}
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="message.content"
                    class="w-full h-24 px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 placeholder-n-slate-10 focus:border-woot-500 focus:outline-none resize-none"
                    placeholder="Digite sua mensagem aqui..."
                    @input="markModified(message)"
                  />
                </div>

                <!-- Template Selection -->
                <div v-else>
                  <label class="text-xs font-medium text-n-slate-11 mb-1 block">Template:</label>
                  <select
                    v-model="message.template_id"
                    class="w-full h-9 px-3 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 focus:border-woot-500 focus:outline-none"
                    @change="markModified(message)"
                  >
                    <option :value="null">Selecione um template...</option>
                    <option
                      v-for="template in whatsappTemplates"
                      :key="template.id"
                      :value="template.id"
                    >
                      {{ template.name }} ({{ template.inbox_name }})
                    </option>
                  </select>
                  <p v-if="whatsappTemplates.length === 0" class="text-xs text-n-slate-10 mt-1">
                    Nenhum template aprovado encontrado nas caixas de entrada WhatsApp.
                  </p>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div
              v-if="messages.length === 0 && !showAddForm"
              class="text-center py-12"
            >
              <span class="i-lucide-message-square-plus size-12 text-n-slate-10 opacity-50" />
              <p class="text-sm text-n-slate-11 mt-3">
                Nenhuma mensagem de follow-up configurada
              </p>
              <p class="text-xs text-n-slate-10 mt-1">
                Adicione mensagens que serão enviadas automaticamente quando uma conversa entrar nesta etapa
              </p>
            </div>

            <!-- Add New Message Form -->
            <div
              v-if="showAddForm"
              class="rounded-lg border-2 border-dashed border-woot-500/50 bg-woot-500/5 p-4"
            >
              <h4 class="text-sm font-medium text-n-slate-12 mb-4">Nova Mensagem</h4>

              <div class="space-y-3">
                <!-- Name -->
                <div>
                  <label class="text-xs font-medium text-n-slate-11 mb-1 block">Nome:</label>
                  <input
                    v-model="newMessage.name"
                    type="text"
                    class="w-full h-9 px-3 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 placeholder-n-slate-10 focus:border-woot-500 focus:outline-none"
                    placeholder="Ex: Mensagem de boas-vindas"
                  />
                </div>

                <!-- Delay -->
                <div class="flex items-center gap-3">
                  <label class="text-xs font-medium text-n-slate-11 w-20">Delay:</label>
                  <select
                    v-model="newMessage.delay_minutes"
                    class="flex-1 h-8 px-3 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 focus:border-woot-500 focus:outline-none"
                  >
                    <option
                      v-for="option in delayOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <!-- Message Type -->
                <div class="flex items-center gap-3">
                  <label class="text-xs font-medium text-n-slate-11 w-20">Tipo:</label>
                  <div class="flex gap-2">
                    <button
                      class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                      :class="newMessage.message_type === 'text' ? 'bg-woot-500 text-white' : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
                      @click="newMessage.message_type = 'text'"
                    >
                      Texto
                    </button>
                    <button
                      class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                      :class="newMessage.message_type === 'template' ? 'bg-woot-500 text-white' : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
                      @click="newMessage.message_type = 'template'"
                    >
                      Template WhatsApp
                    </button>
                  </div>
                </div>

                <!-- Text Content -->
                <div v-if="newMessage.message_type === 'text'">
                  <div class="flex items-center justify-between mb-1">
                    <label class="text-xs font-medium text-n-slate-11">Conteúdo:</label>
                    <div class="flex flex-wrap gap-1">
                      <button
                        v-for="variable in variables"
                        :key="variable.key"
                        class="px-1.5 py-0.5 text-[10px] bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 rounded transition-colors"
                        :title="variable.label"
                        @click="insertVariable(null, variable.key)"
                      >
                        {{ variable.key }}
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="newMessage.content"
                    class="w-full h-24 px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 placeholder-n-slate-10 focus:border-woot-500 focus:outline-none resize-none"
                    placeholder="Digite sua mensagem aqui..."
                  />
                </div>

                <!-- Template Selection -->
                <div v-else>
                  <label class="text-xs font-medium text-n-slate-11 mb-1 block">Template:</label>
                  <select
                    v-model="newMessage.template_id"
                    class="w-full h-9 px-3 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 focus:border-woot-500 focus:outline-none"
                  >
                    <option :value="null">Selecione um template...</option>
                    <option
                      v-for="template in whatsappTemplates"
                      :key="template.id"
                      :value="template.id"
                    >
                      {{ template.name }} ({{ template.inbox_name }})
                    </option>
                  </select>
                </div>

                <!-- Form Actions -->
                <div class="flex justify-end gap-2 pt-2">
                  <button
                    class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors"
                    @click="resetNewMessage"
                  >
                    Cancelar
                  </button>
                  <Button
                    :disabled="isSaving"
                    @click="addMessage"
                  >
                    Adicionar
                  </Button>
                </div>
              </div>
            </div>

            <!-- Add Button -->
            <button
              v-if="!showAddForm"
              class="w-full py-3 border-2 border-dashed border-n-weak hover:border-woot-500 rounded-lg text-sm font-medium text-n-slate-11 hover:text-woot-400 transition-colors flex items-center justify-center gap-2"
              @click="showAddForm = true"
            >
              <span class="i-lucide-plus size-4" />
              Adicionar Mensagem
            </button>
          </div>
        </div>

        <!-- Footer Info -->
        <div class="px-6 py-3 bg-n-alpha-1 border-t border-n-weak">
          <p class="text-xs text-n-slate-10">
            <span class="i-lucide-info size-3 inline-block mr-1 align-text-bottom" />
            Mensagens de texto requerem que o contato tenha interagido nas últimas 24h (janela WhatsApp).
            Templates podem ser enviados a qualquer momento.
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>
